import { Card, CardBody, CardHeader, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { Stat, StatGroup, BarList, EmptyState } from '@vnetwork/ui/data';
import type { OpsResourceSummary } from '@/api/operations-resources';

const DOT_COLORS = [
  'var(--vn-primary-solid)',
  'var(--vn-info-solid)',
  'var(--vn-warning-solid)',
  'var(--vn-success-solid)',
];

/** Title-case a raw status/provider key for display. */
function titleCase(s: string): string {
  if (!s) return '—';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface Props {
  summary: OpsResourceSummary | undefined;
  isLoading: boolean;
  /** e.g. "instances" / "domains" — used in the top-customers card title. */
  kindLabel: string;
}

/**
 * Dashboard-style stat band for a cross-org resource inventory (instances /
 * domains). Top row: total · distinct customers · providers · the two largest
 * status buckets. Below: a Top-customers BarList. Reflects the current filter.
 */
export function OpsResourceStatBand({ summary, isLoading, kindLabel }: Props) {
  const topStatuses = (summary?.by_status ?? []).slice(0, 2);

  const stats = [
    { label: 'Total', value: summary?.total ?? 0, dotColor: DOT_COLORS[0] },
    { label: 'Customers', value: summary?.org_count ?? 0, dotColor: DOT_COLORS[1] },
    { label: 'Providers', value: summary?.by_provider?.length ?? 0, dotColor: DOT_COLORS[2] },
    ...topStatuses.map((s, i) => ({
      label: titleCase(s.key),
      value: s.count,
      dotColor: DOT_COLORS[(i + 3) % DOT_COLORS.length],
    })),
  ];

  const topOrgs = summary?.top_orgs ?? [];
  const bars = topOrgs.map((o) => ({
    label: o.customer?.customer_company_name || o.customer?.customer_name || o.organization_id,
    value: o.count,
  }));

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card flat>
        <CardBody>
          {isLoading ? (
            <StatGroup columns={5}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 56 }} />
              ))}
            </StatGroup>
          ) : (
            <StatGroup columns={5}>
              {stats.map((s) => (
                <Stat
                  key={s.label}
                  label={s.label}
                  dotColor={s.dotColor}
                  value={s.value.toLocaleString('en-US')}
                />
              ))}
            </StatGroup>
          )}
        </CardBody>
      </Card>

      <Card flat>
        <CardHeader>
          <CardTitle>Top customers by {kindLabel}</CardTitle>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Skeleton style={{ height: 120 }} />
          ) : bars.length > 0 ? (
            <BarList items={bars} />
          ) : (
            <EmptyState size="sm" title="No data" />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
